// src/rapportCore.js
// ══════════════════════════════════════════════════════════════════════════
// Cœur partagé du rapport de ménage : capture photo durcie + pipeline de
// sauvegarde (brouillon de secours → recompression → upload Storage → géoloc
// non bloquante → sauvegarde finale).
//
// Extrait pour être réutilisé par ModeGuide.jsx SANS dupliquer la logique et
// SANS toucher au RapportModal durci (ErrorBoundary.jsx). Le comportement est
// identique à celui de RapportModal.save/cap (mêmes limites, mêmes filets).
//
// ⚠️ DETTE (à traiter en session dédiée) : RapportModal conserve encore sa
// copie inline de cap()/save(). Migrer RapportModal vers ce module supprimera
// la duplication — mais ça touche un fichier durci et mérite ses propres tests
// renderToString, donc hors périmètre de la v0 mode guidé.
// ══════════════════════════════════════════════════════════════════════════

import { compressToDataUrl, dataUrlSizeKB, photoLog, photoLogClear, today, now } from './utils.js';
import { enqueueRapport } from './localQueue.js';
import { uploadPhotosToStorage } from './supabase.js';
import { suspendRealtime, resumeRealtime } from './realtime.js';

// ─── Capture photo via input file natif (mobile : caméra arrière directe) ───
// makeCap renvoie une fonction cap(type,id) identique à celle de RapportModal :
// - type='pk' (photo liée à un point) → coche aussi le point automatiquement
// - type='gp' (photo générale de pièce)
// Sécurité caméra Android/MIUI : on suspend le WebSocket Realtime AVANT d'ouvrir
// l'appareil photo et on le reprend au retour (filet : retour de focus si annulation).
export function makeCap({ setPk, setCks, setGp }) {
  return (type, id) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*'; inp.capture = 'environment';
    let _resumed = false;
    const doResume = () => { if (_resumed) return; _resumed = true; setTimeout(() => resumeRealtime(), 1200); window.removeEventListener('focus', onFocusBack); };
    const onFocusBack = () => { setTimeout(doResume, 1500); };
    inp.onchange = async e => {
      const file = e.target.files && e.target.files[0];
      if (!file) { doResume(); return; }
      photoLog('Photo sélectionnée', `${Math.round(file.size / 1024)} Ko · ${file.type || '?'} · point ${id}`);
      const apply = u => {
        photoLog('Compression OK', `${dataUrlSizeKB(u)} Ko finaux`);
        if (type === 'pk') { setPk(p => ({ ...p, [id]: u })); setCks(c => ({ ...c, [id]: true })); }
        else setGp(p => ({ ...p, [id]: u }));
        photoLog('Photo enregistrée'); photoLogClear();
        doResume();
      };
      try {
        photoLog('Décodage + compression démarré (1280/0.7)');
        const u = await compressToDataUrl(file, { maxDim: 1280, quality: 0.7 });
        apply(u);
      } catch (err) {
        photoLog('1ère compression échouée', (err && err.message) || String(err));
        await new Promise(r => setTimeout(r, 400)); // laisse la mémoire respirer avant de retenter
        try {
          photoLog('2e tentative dégradée (800/0.45)');
          const u2 = await compressToDataUrl(file, { maxDim: 800, quality: 0.45 });
          apply(u2);
        } catch (err2) {
          photoLog('2e compression échouée', (err2 && err2.message) || String(err2));
          window.alert('La photo n\'a pas pu être traitée par le téléphone (format ou taille). Réessaie de la reprendre. Si ça recommence, préviens la direction.');
          doResume();
        }
      }
    };
    window.addEventListener('focus', onFocusBack);
    suspendRealtime();
    inp.click();
  };
}

// ─── Re-compresse les photos trop volumineuses avant écriture (filet de sécurité) ───
async function recompressIfNeeded(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (typeof v === 'string' && v.startsWith('data:image') && dataUrlSizeKB(v) > 250) {
      try { out[k] = await compressToDataUrl(v, { maxDim: 1024, quality: 0.6 }); }
      catch (e) { console.error('[rapportCore recompress] failed for', k, e); out[k] = v; }
    } else { out[k] = v; }
  }
  return out;
}

// Calcule le total facturable des extras ménage (bien.bf) — identique à RapportModal.
export function computeBillTotal(bien, bill) {
  return (bien?.bfOn && Array.isArray(bien?.bf))
    ? bien.bf.reduce((sum, field) => {
        const v = bill[field.id];
        if (field.t === 'n') return sum + (Number(v) || 0) * (Number(field.tu) || 0);
        return sum + (v === true ? (Number(field.tu) || 0) : 0);
      }, 0)
    : 0;
}

// ─── Pipeline de sauvegarde du rapport (identique à RapportModal.save) ───
// Produit le MÊME objet rapport {id,sid,tid,bid,date,cks,pk,gp,bill,billTotal,
// notes,sub,geo,mrV}. En mode guidé souple, on y ajoute ecarts[] (points passés /
// pièces ouvertes hors ordre) + guideMode ('souple'|'strict') pour la trace admin.
// onSave(rapport, fin) est appelé (a) tôt pour le brouillon
// de secours, (b) à la fin avec les photos uploadées en Storage.
// req = fonction de géoloc issue de useGeo() (appelée dans le composant).
// Retourne {nonSync} : nombre de photos restées en base64 (upload Storage raté).
export async function buildAndSaveRapport({ existing, sid, task, bien, cks, pk, gp, bill, notes, useNew, onSave, req, fin, ecarts = [], guideMode = null, authorLabel = '', engage = false }) {
  const repId = existing?.id || 'rep' + Date.now();
  const billTotal = computeBillTotal(bien, bill);
  // ─── Engagement salarié (v159, 14/08/2026, demande Claire) : case cochée à la TOUTE
  // PREMIÈRE soumission uniquement (existing?.sub encore vide). Une fois posé, l'engagement
  // est reporté tel quel à chaque réécriture ultérieure (brouillon, correction admin…) —
  // jamais réécrasé, jamais réexigé une deuxième fois pour le même rapport.
  const firstSubmission = !existing?.sub;
  const engageOut = existing?.engage || ((fin && firstSubmission && engage) ? true : undefined);
  const engageTsOut = existing?.engageTs || ((fin && firstSubmission && engage) ? now() : undefined);
  // ─── FILET DE SÉCURITÉ : brouillon sauvegardé AVANT toute opération risquée ───
  try {
    const brouillon = { id: repId, sid, tid: task?.id, bid: bien?.id, date: today(), cks, pk, gp, bill, billTotal, notes, sub: existing?.sub || null, geo: existing?.geo || null, mrV: useNew ? 'v2' : 'v1', ecarts, guideMode, engage: engageOut, engageTs: engageTsOut };
    onSave(brouillon, false);
  } catch (e) { console.error('[rapportCore] brouillon de secours échoué (non bloquant)', e); }
  // ─── FILE D'ATTENTE LOCALE (SPEC_file_locale_v1 §5.2) : écrite sur le disque du terminal
  // AVANT toute opération réseau/coûteuse. Non bloquant : si la file échoue, le pipeline
  // réseau ci-dessous s'exécute inchangé. Confirmation par recordId dans supabase.js (§5.3). ───
  try {
    const qPayload = { id: repId, sid, tid: task?.id, bid: bien?.id, date: today(), cks, bill, billTotal, notes, sub: fin ? now() : (existing?.sub || null), geo: existing?.geo || null, mrV: useNew ? 'v2' : 'v1', ecarts, guideMode, engage: engageOut, engageTs: engageTsOut };
    await enqueueRapport({ recordId: repId, payload: qPayload, pk, gp, sid, authorLabel, fin });
  } catch (e) { console.error('[rapportCore] enqueue file locale échoué (non bloquant)', e); }
  // Re-compression + upload Storage (jamais de base64 dans le rapport final)
  const newPk = await recompressIfNeeded(pk);
  const newGp = await recompressIfNeeded(gp);
  const storagePk = await uploadPhotosToStorage(newPk, repId, 'pk');
  const storageGp = await uploadPhotosToStorage(newGp, repId, 'gp');
  const countBase64 = obj => Object.values(obj || {}).filter(v => typeof v === 'string' && v.startsWith('data:image')).length;
  const nonSync = countBase64(storagePk) + countBase64(storageGp);
  // ─── GÉOLOC NON BLOQUANTE (timeout 8s, sauvegarde dans tous les cas) ───
  const geo = await new Promise(resolve => {
    let done = false;
    const finish = g => { if (!done) { done = true; resolve(g); } };
    setTimeout(() => finish(null), 8000);
    try { req(g => finish(g || null)); }
    catch (e) { console.error('[rapportCore] géoloc indisponible (non bloquant)', e); finish(null); }
  });
  const r = { id: repId, sid, tid: task?.id, bid: bien?.id, date: today(), cks, pk: storagePk, gp: storageGp, bill, billTotal, notes, sub: fin ? now() : null, geo: geo || null, mrV: useNew ? 'v2' : 'v1', ecarts, guideMode, engage: engageOut, engageTs: engageTsOut };
  onSave(r, fin);
  return { nonSync };
}
